import '../components';
import { $, showError, setDisabled } from '../utils';
import type { LogBoxElement } from '../types';

let isSending = false;
let stopRequested = false;

const sendBtn = $('sendBtn');
const stopBtn = $('stopBtn');
const logBox = $<LogBoxElement>('logBox');
const statusText = $('statusText');
const webhookInput = $<HTMLInputElement>('webhook');
const messageInput = $<HTMLTextAreaElement>('message');
const countInput = $<HTMLInputElement>('count');
const rateInput = $<HTMLInputElement>('rate');

function updateButtons(): void {
  setDisabled(sendBtn, isSending);
  setDisabled(stopBtn, !isSending);
}

function clearLog(): void {
  logBox.clear();
  statusText.textContent = 'Status: Ready';
}

function stopSending(): void {
  if (!isSending) return;
  stopRequested = true;
  logBox.addLog('Stop requested...', 'info');
}

async function sendMessages(): Promise<void> {
  if (isSending) {
    logBox.addLog('Already sending messages!', 'error');
    return;
  }

  isSending = true;
  stopRequested = false;
  updateButtons();

  const webhook = webhookInput.value.trim();
  const message = messageInput.value;
  const count = parseInt(countInput.value, 10);
  const rate = parseFloat(rateInput.value);
  const delay = 1000 / rate;

  if (!webhook) {
    logBox.addLog('Error: Webhook URL is required!', 'error');
    isSending = false;
    updateButtons();
    return;
  }

  logBox.addLog(`Starting to send ${count} message(s) at ${rate} msg/s`, 'info');
  statusText.textContent = 'Status: Sending...';

  let sent = 0;
  let failed = 0;

  for (let i = 0; i < count; i++) {
    if (stopRequested) {
      logBox.addLog('Stopped by user', 'info');
      break;
    }

    try {
      const response = await fetch('/send?webhook=' + encodeURIComponent(webhook), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: `${message} (#${i + 1})` }),
      });

      if (response.ok) {
        sent++;
        logBox.addLog(`✓ Message #${i + 1} sent successfully`, 'success');
      } else {
        failed++;
        const error = await response.text();
        logBox.addLog(`✗ Message #${i + 1} failed: ${error}`, 'error');
      }
    } catch (error) {
      failed++;
      const msg = error instanceof Error ? error.message : 'Unknown error';
      logBox.addLog(`✗ Message #${i + 1} error: ${msg}`, 'error');
    }

    if (i < count - 1 && !stopRequested) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  isSending = false;
  updateButtons();
  const status = stopRequested ? 'Stopped' : 'Complete';
  statusText.textContent = `Status: ${status}`;
  logBox.addLog(`Finished: ${sent} sent, ${failed} failed`, 'info');
}

updateButtons();

declare global {
  interface Window {
    sendMessages: typeof sendMessages;
    stopSending: typeof stopSending;
    clearLog: typeof clearLog;
  }
}

window.sendMessages = sendMessages;
window.stopSending = stopSending;
window.clearLog = clearLog;
